/**
 * KISSED University · Post-Square return page.
 * Routes: /university/payment/return AND /university/payment/cancel
 *
 * Square's static payment link does not auto-redirect back to a known URL.
 * Applicants may bookmark this page or arrive via a manual link. We:
 *   - Show their saved application_id + email (from sessionStorage)
 *   - Let them re-open Square if they didn't pay yet
 *   - Send them to the Status page to verify state
 *
 * Reconciliation is manual (admin marks "Paid" in the review queue once Square
 * confirms the transaction).
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, CreditCard, Loader2, ChevronRight, AlertCircle } from "lucide-react";
import logo from "@/assets/brand/kissed-logo.png";
import { Button } from "@/components/ui/button";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";

export default function UniversityPaymentReturn() {
  const [params] = useSearchParams();
  const queryAppId = params.get("application_id") || "";
  const queryEmail = params.get("email") || "";

  const [applicationId] = useState(() =>
    queryAppId || sessionStorage.getItem("kissed_uni_last_application_id") || ""
  );
  const [email] = useState(() =>
    queryEmail || sessionStorage.getItem("kissed_uni_last_email") || ""
  );

  const [opts, setOpts] = useState(null);
  const [statusData, setStatusData] = useState(null);
  const [restarting, setRestarting] = useState(false);

  useEffect(() => {
    universityApi.options().then((r) => setOpts(r.data)).catch(() => setOpts(null));
    if (applicationId) {
      universityApi
        .paymentStatus(applicationId, { email: email || undefined })
        .then((r) => setStatusData(r.data))
        .catch(() => setStatusData(null));
    }
  }, [applicationId, email]);

  const restartPayment = async () => {
    if (!applicationId || !email) return;
    setRestarting(true);
    try {
      const r = await universityApi.startCheckout(applicationId, {
        application_id: applicationId, email, origin_url: window.location.origin,
      });
      const url = r?.data?.url;
      if (!url) return;
      window.location.href = url;
    } catch (err) {
      console.error(formatApiError(err, "restart payment failed"));
    } finally {
      setRestarting(false);
    }
  };

  const feeStatus = statusData?.application_fee_status || "Pending";
  const paid = feeStatus === "Paid" || feeStatus === "Waived";

  return (
    <div data-testid="university-payment-return" className="min-h-screen bg-[#faf7f5]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/university"><img src={logo} alt="KISSED" className="h-11 w-auto drop-shadow-md" /></Link>
          <span className="font-display text-sm text-slate-700">Application payment</span>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-6 py-12 space-y-5">
        {!applicationId ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6" data-testid="pay-missing-id">
            <AlertCircle className="w-7 h-7 text-red-700" />
            <h2 className="mt-3 font-display text-2xl text-red-900">We can't find your application</h2>
            <p className="mt-2 text-sm text-red-800">Please use the link in your confirmation email or visit the status page to look up by Application ID + email.</p>
            <div className="mt-4"><Button asChild variant="outline"><Link to="/university/status">Go to status page</Link></Button></div>
          </div>
        ) : paid ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6" data-testid="pay-paid">
            <CheckCircle2 className="w-8 h-8 text-emerald-700" />
            <h2 className="mt-3 font-display text-2xl text-emerald-900">Payment received</h2>
            <p className="mt-2 text-sm text-emerald-800">Thank you. Your $50 application fee has been received and your application is now submitted for admissions review. You'll hear from us within {opts?.review_window_days || "3–5 business days"}.</p>
            <div className="mt-4">
              <Button asChild className="bg-purple-900 hover:bg-purple-800">
                <Link to={`/university/status?id=${encodeURIComponent(applicationId)}${email ? `&email=${encodeURIComponent(email)}` : ""}`} data-testid="pay-status-link">
                  View your status <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-purple-200 bg-white p-6" data-testid="pay-instructions">
            <CreditCard className="w-8 h-8 text-purple-900" />
            <h2 className="mt-3 font-display text-2xl text-slate-900">Complete your $50 application fee on Square</h2>
            <p className="mt-2 text-sm text-slate-700 leading-relaxed">
              A non-refundable $50 application fee is required to submit your application for admissions review.
              Once you complete payment on Square, our team will manually reconcile the transaction and update
              your status to <em>Submitted</em>. This usually happens within one business day.
            </p>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <Button
                className="bg-purple-900 hover:bg-purple-800"
                disabled={restarting || !email}
                onClick={restartPayment}
                data-testid="pay-restart-square"
              >
                {restarting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1" />}
                Open Square again
              </Button>
              <Button asChild variant="outline">
                <Link to={`/university/status?id=${encodeURIComponent(applicationId)}${email ? `&email=${encodeURIComponent(email)}` : ""}`} data-testid="pay-status-link">
                  Check status <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>
            <div className="mt-5 text-xs text-slate-500 space-y-1">
              <div>Application ID: <span className="font-mono">{applicationId}</span></div>
              {email ? <div>Email: {email}</div> : null}
              <div>Fee status: {feeStatus}</div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
